import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Tractor, Layers, Loader2, ArrowRight, Wrench } from 'lucide-react';

const iconMap = {
  Flame: Flame,
  Tractor: Tractor,
  Layers: Layers,
  Wrench: Wrench,
};

// Fallback data with real assets in case the API is loading or has an issue
const fallbackServices = [
  {
    id: '3',
    slug: 'professionel-montage',
    title: { da: "Montagearbejde" },
    description: { da: "<p>Vi tilbyder komplet montagearbejde af stålkonstruktioner, maskinanlæg, rørledninger og meget mere. Vi rykker ud til både store og små opgaver og sikrer en sikker, præcis og hurtig montering på stedet.</p><p>Med Balsby Montage som din samarbejdspartner er du garanteret et professionelt resultat, der lever op to alle specifikationer og tidsplaner.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/d51a1730-b243-4fb1-b541-eb82f9db479c.jpg"],
    icon_name: "Layers",
    order: 1
  },
  {
    id: '1',
    slug: 'professionelt-svejsearbejde',
    title: { da: "Professionelt Svejsearbejde" },
    description: { da: "<p>Vi tilbyder professionelt og certificeret svejsearbejde i højeste kvalitet. Uanset om det drejer sig om rustfrit stål, aluminium eller sort jern, sikrer vi præcise og holdbare samlinger til både industri, byggeri og private projekter.</p><p>Vores ekspertise dækker TIG-, MIG- og MAG-svejsning, og vi går aldrig på kompromis med kvaliteten eller sikkerheden.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/4c8d59aa-f867-43d4-a5a9-779f6b9d3841.jpg"],
    icon_name: "Flame",
    order: 2
  },
  {
    id: '2',
    slug: 'reparation-af-landbrugsmaskiner',
    title: { da: "Reparation af landbrugsmaskiner" },
    description: { da: "<p>Vi tilbyder professionel reparation og vedligeholdelse af alle typer landbrugsmaskiner og traktorer. Med stor erfaring inden for landbrugssektoren sikrer vi, at din maskinpark altid kører optimalt, så du undgår dyre driftsstop i højsæsonen.</p><p>Uanset om det drejer sig om mekanisk arbejde, hydraulik, svejsning af sliddele eller akut fejlfinding, rykker vi hurtigt ud og løser opgaven med høj præcision.</p>" },
    image: ["https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/reparation-af-landbrugsmaskiner.jpg"],
    icon_name: "Tractor",
    order: 3
  }
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setServices(sorted);
            setActiveTab(sorted[0]?.id || null);
          } else {
            setServices(fallbackServices);
            setActiveTab(fallbackServices[0].id);
          }
        } else {
          setServices(fallbackServices);
          setActiveTab(fallbackServices[0].id);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices(fallbackServices);
        setActiveTab(fallbackServices[0].id);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const handleSelectService = (e) => {
      const serviceId = e.detail;
      if (serviceId) {
        setActiveTab(serviceId);
        
        // Scroll to section with offset
        const element = document.getElementById('ydelser');
        if (element) {
          const offset = 80;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    window.addEventListener('select-service', handleSelectService);
    return () => window.removeEventListener('select-service', handleSelectService);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#487aad]" />
      </div>
    );
  }

  const activeService = services.find(s => s.id === activeTab) || services[0];

  return (
    <section id="ydelser" className="py-24 bg-white relative overflow-hidden">
      {/* Background glow shape */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#487aad]/3 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-left max-w-3xl mb-16">
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-black uppercase">
            Vores Ydelser & Kompetencer
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Vi leverer professionelle løsninger inden for en bred vifte af tekniske og håndværksmæssige områder. Klik på ydelserne for at læse mere.
          </p>
        </div>

        {/* Desktop Layout (Tabs + Detail) */}
        <div className="hidden lg:grid grid-cols-12 gap-8 items-start">
          {/* Tabs List */}
          <div className="col-span-4 space-y-3">
            {services.map((service) => {
              const isActive = service.id === activeTab;
              return (
                <button
                  key={service.id}
                  onClick={() => setActiveTab(service.id)}
                  className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-black text-white font-bold shadow-md'
                      : 'bg-white hover:bg-slate-50 border border-slate-100 text-black shadow-sm hover:shadow-md'
                  }`}
                >
                  <span className="text-base font-semibold">
                    {service.title?.da || service.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active Tab Content */}
          {activeService && (
            <div className="col-span-8 bg-slate-50 border border-slate-200/60 shadow-sm grid grid-cols-1 md:grid-cols-2 min-h-[450px] items-stretch overflow-hidden">
              <div className="flex flex-col justify-center p-8 md:p-10">
                <h3 className="text-2xl font-black text-black uppercase mb-4">
                  {activeService.title?.da || activeService.title}
                </h3>
                <div 
                  className="text-slate-600 space-y-4 text-sm leading-relaxed prose prose-slate mb-6"
                  dangerouslySetInnerHTML={{ __html: activeService.description?.da || activeService.description }}
                />
                <Link
                  to={`/ydelse/${activeService.slug?.da || activeService.slug}`}
                  className="inline-flex items-center gap-2 bg-black hover:bg-zinc-900 text-white font-bold px-6 py-3 text-xs uppercase tracking-wider transition-all duration-200 w-fit cursor-pointer"
                >
                  <span>Læs mere om ydelsen</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="relative min-h-[320px] md:min-h-0 bg-slate-100">
                <img
                  src={activeService.image?.[0]}
                  alt={activeService.title?.da || activeService.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-103"
                />
              </div>
            </div>
          )}
        </div>

        {/* Mobile Layout (Cards List) */}
        <div className="lg:hidden space-y-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon_name] || Wrench;
            const sSlug = service.slug?.da || service.slug || '';
            return (
              <div 
                key={service.id}
                className="bg-slate-50 border border-slate-200/60 overflow-hidden shadow-sm flex flex-col"
              >
                <div className="h-52 relative bg-slate-100">
                  <img
                    src={service.image?.[0]}
                    alt={service.title?.da || service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-black text-black uppercase mb-3">
                      {service.title?.da || service.title}
                    </h3>
                    <div 
                      className="text-slate-600 text-sm leading-relaxed space-y-3 prose prose-slate mb-4 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: service.description?.da || service.description }}
                    />
                  </div>
                  <Link
                    to={`/ydelse/${sSlug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#487aad] hover:text-[#3b6591] transition-colors mt-2"
                  >
                    <span>Læs mere</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
